import React from 'react';
import { Lock, Zap, Lightbulb, Star, Moon, Square, Users, Heart, Sun, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ModuleRow = ({ num, title, icon: Icon, t }) => (
  <div 
    className="bg-[#1a1f2e] rounded-lg p-4 flex items-center justify-between hover:bg-[#2a3142] transition-colors border border-gray-700/50"
    data-testid={`module-${num}`}
  >
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 bg-[#d4a33a] rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-white font-medium">Module {num}: {title}</span>
    </div>
    <button className="flex items-center gap-2 px-4 py-2 bg-[#d4a33a]/10 text-[#d4a33a] rounded-lg text-sm font-medium hover:bg-[#d4a33a]/20 transition-colors flex-shrink-0">
      <Lock className="w-4 h-4" />
      {t('unlock')}
    </button>
  </div>
);

const PartHeader = ({ partNum, title, subtitle }) => (
  <div className="p-6 border-b border-gray-700 bg-[#2a3142]/50">
    <div className="flex items-center gap-4">
      <span className="px-3 py-1.5 bg-[#d4a33a]/20 text-[#d4a33a] text-xs font-bold rounded-lg">
        Part {partNum}
      </span>
      <div>
        <h3 className="text-xl font-bold text-white">{title}</h3>
        <p className="text-gray-400 text-sm">{subtitle}</p>
      </div>
    </div>
  </div>
);

const CurriculumSection = () => {
  const { t } = useLanguage();

  return (
    <section id="curriculum" className="py-16 md:py-24 bg-[#1a1f2e]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('courseCurriculum')}</h2>
          <p className="text-gray-400 text-lg">{t('curriculumDesc')}</p>
        </div>

        <div className="space-y-8">
          {/* Part 1 */}
          <div className="bg-[#232836] rounded-xl overflow-hidden border border-gray-700">
            <PartHeader partNum={1} title={t('part1Title')} subtitle={t('part1Subtitle')} />
            <div className="p-4 space-y-3">
              <ModuleRow num={1} title="The Call to Consciousness: An Invitation to Awakening" icon={Zap} t={t} />
              <ModuleRow num={2} title="Introduction to Energy Healing" icon={Lightbulb} t={t} />
              <ModuleRow num={3} title="Understanding Masculine Energy" icon={Star} t={t} />
              <ModuleRow num={4} title="Understanding Feminine Energy" icon={Moon} t={t} />
              <ModuleRow num={5} title="Chakra Balancing Basics" icon={Heart} t={t} />
            </div>
          </div>

          {/* Part 2 */}
          <div className="bg-[#232836] rounded-xl overflow-hidden border border-gray-700">
            <PartHeader partNum={2} title={t('part2Title')} subtitle={t('part2Subtitle')} />
            <div className="p-4 space-y-3">
              <ModuleRow num={6} title="Daily Integration Practices" icon={Square} t={t} />
              <ModuleRow num={7} title="The Warrior Archetype" icon={Lightbulb} t={t} />
              <ModuleRow num={8} title="Sacred Boundaries" icon={Star} t={t} />
              <ModuleRow num={9} title="Purpose and Direction" icon={Zap} t={t} />
              <ModuleRow num={10} title="The Protector Within" icon={Moon} t={t} />
            </div>
          </div>

          {/* Part 3 */}
          <div className="bg-[#232836] rounded-xl overflow-hidden border border-gray-700">
            <PartHeader partNum={3} title={t('part3Title')} subtitle={t('part3Subtitle')} />
            <div className="p-4 space-y-3">
              <ModuleRow num={11} title="Intuition Awakening" icon={Sparkles} t={t} />
              <ModuleRow num={12} title="Creative Flow States" icon={Square} t={t} />
              <ModuleRow num={13} title="Emotional Intelligence" icon={Lightbulb} t={t} />
              <ModuleRow num={14} title="Nurturing Energy" icon={Users} t={t} />
              <ModuleRow num={15} title="Receptivity Practice" icon={Heart} t={t} />
            </div>
          </div>

          {/* Part 4 */}
          <div className="bg-[#232836] rounded-xl overflow-hidden border border-gray-700">
            <PartHeader partNum={4} title={t('part4Title')} subtitle={t('part4Subtitle')} />
            <div className="p-4 space-y-3">
              <ModuleRow num={16} title="Sacred Union Within" icon={Sun} t={t} />
              <ModuleRow num={17} title="Relationship Alchemy" icon={Lightbulb} t={t} />
              <ModuleRow num={18} title="Polarity Mastery" icon={Star} t={t} />
              <ModuleRow num={19} title="Living in Balance" icon={Zap} t={t} />
              <ModuleRow num={20} title="The Integrated Self" icon={Sparkles} t={t} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CurriculumSection;
